import type { LabelInValue, UserList } from 'models';
import { type ComponentProps, useMemo, useState } from 'react';
import { InfinityScrollSelect } from 'ui';
import { useDebounce } from 'common/hooks';
import { useUsersInfiniteQuery } from '../../queries';

export type UserSelectProperties<T extends LabelInValue> = {
  createOption: (value: UserList) => T;
  query?: Partial<Parameters<typeof useUsersInfiniteQuery>[0]>;
} & Omit<ComponentProps<typeof InfinityScrollSelect<T>>, 'handleRequestMoreData' | 'hasMoreData'>;

const UserSelect = <T extends LabelInValue>({ createOption, query, ...props }: UserSelectProperties<T>) => {
  const [search, setSearch] = useState<string>();
  const { isDebouncing, value } = useDebounce(search);

  const {
    data: users,
    fetchNextPage: fetchNextPageOfUsers,
    hasNextPage: hasMoreUsers,
    isFetching: isFetchingUsers,
  } = useUsersInfiniteQuery({
    ...query,
    pageSize: query?.pageSize ?? 10,
    search: value,
  });

  const options = useMemo(
    () => users?.pages.flatMap((page) => page.map((user) => createOption(user))) ?? [],
    [createOption, users],
  );

  return (
    <InfinityScrollSelect<T>
      handleRequestMoreData={fetchNextPageOfUsers}
      hasMoreData={hasMoreUsers}
      loading={isFetchingUsers || isDebouncing}
      onSearch={setSearch}
      options={options}
      {...props}
    />
  );
};

export default UserSelect;
